import { memo, useCallback } from 'react'

import { FileText } from '@gravity-ui/icons'
import { EmptyState, Table } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import { Pagination } from '@/components/ui/Pagination'
import { RowsPerPage } from '@/components/ui/RowsPerPage'
import { useTimesheetListStore } from '@/store/timesheetList.store'
import type { TimeReport } from '@/types/timeReport'

interface TimesheetTableProps {
  reports: TimeReport[];
  onView?: (id: string) => void;
}

interface TimesheetRowProps {
  report: TimeReport;
  onView?: (id: string) => void;
  language?: string;
}

const TimesheetRow = memo(({ report, onView, language }: TimesheetRowProps) => {
  const handleView = useCallback(() => {
    onView?.(report.ID)
  }, [onView, report.ID])

  return (
    <Table.Row className="cursor-pointer">
      <Table.Cell onClick={handleView}>
        {report.Driver?.Name ?? '-'}
      </Table.Cell>
      <Table.Cell onClick={handleView}>
        {report.Month && report.Year
          ? new Date(report.Year, report.Month - 1).toLocaleString(
              language === 'ru' ? 'ru-RU' : 'en-US',
              { month: 'long', year: 'numeric' }
            )
          : '-'}
      </Table.Cell>
    </Table.Row>
  )
})

// eslint-disable-next-line better-mutation/no-mutation
TimesheetRow.displayName = 'TimesheetRow'

export function TimesheetTable({ reports, onView }: TimesheetTableProps) {
  const { t, i18n } = useTranslation()
  const { pagination, setPagination } = useTimesheetListStore()

  const handlePageChange = useCallback((page: number) => {
    setPagination({ page })
  }, [setPagination])

  const handleLimitChange = useCallback((limit: number) => {
    setPagination({ limit, page: 0 })
  }, [setPagination])

  const renderEmptyState = useCallback(() => (
    <EmptyState className="flex h-30 mt-6 w-full flex-col items-center justify-center gap-4 text-center">
      <FileText className="size-6 text-zinc-400" />
      <span className="text-sm text-zinc-500">{t('timesheet.no_reports')}</span>
    </EmptyState>
  ), [t])

  const start = pagination.total === 0 ? 0 : pagination.page * pagination.limit + 1
  const end = Math.min((pagination.page + 1) * pagination.limit, pagination.total)

  return (
    <Table className="min-h-[200px]">
      <Table.ScrollContainer className="max-h-[calc(100vh-theme(spacing.48))]">
        <Table.Content
          aria-label="Timesheets list table"
          className="min-w-[800px]"
        >
          <Table.Header className="sticky top-0 z-10">
            <Table.Column id="Driver.Name" isRowHeader>
              {t('timesheet.driver')}
            </Table.Column>
            <Table.Column id="Year">
              {t('common.period')}
            </Table.Column>
          </Table.Header>
          <Table.Body renderEmptyState={renderEmptyState}>
            {reports.map(r => (
              <TimesheetRow
                key={r.ID}
                report={r}
                onView={onView}
                language={i18n.resolvedLanguage}
              />
            ))}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
      <Table.Footer>
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-4">
            <RowsPerPage
              value={pagination.limit}
              onChange={handleLimitChange}
            />
            <span className="text-xs text-foreground/50">
              {t('common.pagination_summary', { start, end, total: pagination.total })}
            </span>
          </div>
          <Pagination
            page={pagination.page}
            total={pagination.totalPages}
            onChange={handlePageChange}
          />
        </div>
      </Table.Footer>
    </Table>
  )
}
